import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Flex,
  Avatar,
  Box,
  VStack,
  useToast,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Key } from 'phosphor-react';
import { useSolicitacaoAcesso, ISolicitacaoAcesso } from '../../hooks/useSolicitacaoAcesso';
import { useSolicitacaoTokens } from '../../hooks/useSolicitacaoTokens';
import { IDataNoPagination } from '../../types/shared';

interface SolicitacaoAcessoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Função para formatar a duração em texto legível
const formatarDuracao = (horas: number | undefined | null): string => {
  if (!horas) return '7 dias'; // Default

  if (horas < 24) {
    return horas === 1 ? '1 hora' : `${horas} horas`;
  }

  const dias = Math.floor(horas / 24);
  if (dias === 1) return '1 dia';
  if (dias === 7) return '7 dias';
  return `${dias} dias`;
};

export function SolicitacaoAcessoModal({ isOpen, onClose }: SolicitacaoAcessoModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { getPendentes, aprovarSolicitacao } = useSolicitacaoAcesso();
  const { saveToken } = useSolicitacaoTokens();
  const [tokenAprovado, setTokenAprovado] = useState<{ id: number; token: string } | null>(null);

  const { data, isLoading, refetch } = useQuery<IDataNoPagination<ISolicitacaoAcesso[]>>({
    queryKey: ['solicitacoes-pendentes'],
    queryFn: getPendentes,
    enabled: isOpen,
  });

  const solicitacoes = data?.data ?? [];

  const { mutateAsync: aprovar, isPending: isAprovando } = useMutation({
    mutationFn: (id: number) => aprovarSolicitacao(id),
    onSuccess: (response, solicitacaoId) => {
      // response já é IDataNoPagination<ISolicitacaoAcesso>, então response.data é ISolicitacaoAcesso
      const solicitacao = response?.data;
      const idDaResposta = solicitacao?.id || solicitacaoId;
      const token = solicitacao?.token_acesso;

      if (token && idDaResposta) {
        setTokenAprovado({ id: idDaResposta, token });

        // Salva o token usando o hook (backup caso o interceptor não funcione)
        saveToken(idDaResposta, token);

        // Dispara evento customizado para atualizar outras partes da aplicação
        window.dispatchEvent(
          new CustomEvent('token-recebido', {
            detail: { solicitacao_id: idDaResposta, token },
          }),
        );

        toast({
          title: 'Acesso aprovado!',
          description: 'O token foi gerado e enviado ao administrador.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['solicitacoes-pendentes'] });
      queryClient.invalidateQueries({ queryKey: ['minhas-solicitacoes'] });
      refetch();
    },
    onError: () => {
      toast({
        title: 'Erro ao aprovar',
        description: 'Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
  const copyToClipboard = (token: string) => {
    navigator.clipboard.writeText(token);
    toast({
      title: 'Token copiado!',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClose = () => {
    setTokenAprovado(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      isCentered
      closeOnOverlayClick={solicitacoes.length === 0} // Só permite fechar clicando fora se não houver solicitações
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader>
          <Flex alignItems="center" gap={2}>
            <Key size={24} weight="bold" />
            <Text>Solicitações de Acesso</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isLoading && (
            <Flex justify="center" py={8}>
              <Spinner size="lg" />
            </Flex>
          )}
          {!isLoading && solicitacoes.length === 0 && (
            <Flex direction="column" align="center" py={8}>
              <Text color="gray.500">Nenhuma solicitação pendente</Text>
            </Flex>
          )}
          {!isLoading && solicitacoes.length > 0 && (
            <VStack spacing={4} align="stretch">
              {solicitacoes.map((solicitacao: ISolicitacaoAcesso) => (
                <Box
                  key={solicitacao.id}
                  p={4}
                  borderWidth={1}
                  borderRadius="lg"
                  borderColor="gray.200"
                >
                  <Flex justify="space-between" align="center" mb={3}>
                    <Flex align="center" gap={3}>
                      <Avatar
                        size="md"
                        name={solicitacao.solicitante?.nome}
                        src={solicitacao.solicitante?.imagem_url}
                      />
                      <Box>
                        <Text fontWeight="bold">{solicitacao.solicitante?.nome}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {solicitacao.solicitante?.email}
                        </Text>
                      </Box>
                    </Flex>
                    <Badge colorScheme="yellow">Pendente</Badge>
                  </Flex>

                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Está solicitando acesso à sua conta. Se você aprovar, este administrador poderá
                    acessar sua conta por{' '}
                    <strong>{formatarDuracao(solicitacao.duracao_horas)}</strong>.
                  </Text>

                  <Text fontSize="xs" color="gray.400" mb={3}>
                    Solicitado em: {new Date(solicitacao.dt_criacao).toLocaleString('pt-BR')}
                  </Text>

                  {tokenAprovado?.id === solicitacao.id ? (
                    <Box bg="green.50" p={3} borderRadius="md">
                      <Flex align="center" gap={2} mb={2}>
                        <Check size={20} color="#38A169" />
                        <Text color="green.600" fontWeight="bold">
                          Aprovado!
                        </Text>
                      </Flex>
                      <Text fontSize="sm" mb={2}>
                        Token gerado para o administrador:
                      </Text>
                      <Flex
                        align="center"
                        gap={2}
                        bg="white"
                        p={2}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="green.200"
                      >
                        <Text fontFamily="mono" fontWeight="bold" fontSize="lg">
                          {tokenAprovado?.token}
                        </Text>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => tokenAprovado && copyToClipboard(tokenAprovado.token)}
                        >
                          <Copy size={16} />
                        </Button>
                      </Flex>
                    </Box>
                  ) : (
                    <Button
                      colorScheme="green"
                      size="sm"
                      leftIcon={<Check size={18} />}
                      onClick={() => aprovar(solicitacao.id)}
                      isLoading={isAprovando}
                      width="100%"
                    >
                      Permitir
                    </Button>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
